import { ChevronRight, Lock } from 'lucide-react'
import { Link } from 'react-router'
import { Avatar } from '@/components/tree/Avatar'
import { circleStyle } from '@/lib/circleColor'
import { isPrivate, momentPhotoUrl, paragraphs, written, type MomentShelf, type MomentSummary } from '@/lib/moments'
import { firstName } from '@/lib/tree'
import { cn } from '@/lib/utils'
import { colorOf } from '../../../shared/circleColors'
import { shapeOf } from '../../../shared/momentStyle'

const link = 'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'

// Circles and People: a short row for each circle (or space) or each person, its newest stories side by side to swipe
// through, like Home's Wander shelves.
export function MomentShelves({ shelves, label }: { shelves: MomentShelf[]; label: string }) {
  return (
    <div className="flex flex-col gap-8" role="list" aria-label={label}>
      {shelves.map((shelf) => (
        <ShelfRow key={shelf.key} shelf={shelf} />
      ))}
    </div>
  )
}

function ShelfRow({ shelf }: { shelf: MomentShelf }) {
  const name = shelf.kind === 'person' && shelf.moments[0]?.mine ? 'You' : shelf.name
  const heading = (
    <>
      {shelf.kind === 'person' && shelf.author ? (
        <Avatar person={shelf.author} className="size-9 text-xs shadow-none" />
      ) : shelf.kind === 'circle' ? (
        <span
          aria-hidden
          style={circleStyle(colorOf(shelf.color))}
          className="size-3 shrink-0 rounded-full bg-(--circle)"
        />
      ) : null}
      <span className="truncate text-lg font-semibold tracking-tight">{name}</span>
    </>
  )
  return (
    <section role="listitem" aria-label={name} className="flex flex-col gap-3">
      {shelf.kind === 'circle' ? (
        <Link
          to={`/circle/${shelf.key}/moments`}
          className={cn('group flex min-w-0 items-center gap-2.5 self-start rounded-xl pr-2', link)}
        >
          {heading}
          <ChevronRight className="size-5 shrink-0 text-muted-foreground" aria-hidden />
        </Link>
      ) : (
        <div className="flex min-w-0 items-center gap-2.5">{heading}</div>
      )}
      <ul className="-mx-5 flex snap-x scroll-px-5 [scrollbar-width:none] gap-3 overflow-x-auto px-5 pb-2 sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
        {shelf.moments.map((moment) => (
          <li key={moment.id} className="w-40 shrink-0 snap-start sm:w-48">
            <StoryCard moment={moment} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function StoryCard({ moment }: { moment: MomentSummary }) {
  const photo = shapeOf(moment) === 'words' ? undefined : moment.photos[0]
  const frame = 'block aspect-[4/5] w-full overflow-hidden rounded-2xl bg-secondary shadow-paper'
  return (
    <Link to={`/moment/${moment.id}`} className={cn('flex flex-col gap-1.5 rounded-2xl', link)}>
      <span className={cn(frame, !photo && 'flex flex-col bg-card')}>
        {photo ? (
          <img
            src={momentPhotoUrl(photo.id, 'thumb')}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover"
          />
        ) : (
          <>
            <span aria-hidden="true" className="words-bar h-1.5 shrink-0" />
            <span className="line-clamp-8 p-3 text-sub text-pretty text-muted-foreground">
              {moment.body.trim() ? paragraphs(moment.body)[0] : moment.title}
            </span>
          </>
        )}
      </span>
      <span className="line-clamp-2 px-0.5 text-sub leading-snug font-semibold">{moment.title}</span>
      <span className="flex items-center gap-1 px-0.5 text-xs text-muted-foreground">
        {isPrivate(moment) && <Lock className="size-3 shrink-0" aria-label="Only you" />}
        <span className="truncate">
          {moment.mine ? 'You' : firstName(moment.author.name)} · {written(moment.sharedAt ?? moment.createdAt)}
        </span>
      </span>
    </Link>
  )
}
